/**
 * Serializer for URL-driven filter DSL.
 * Converts structured FilterSet to URL query parameter string.
 *
 * Output format: facet:op(values)&facet2:op(values)
 * - Returns unencoded string (URLSearchParams will handle encoding)
 * - Uses ampersand (&) to separate clauses (AND logic)
 * - Uses parentheses for values
 */

import type { FilterSet, FilterClause } from './types';

/**
 * Serializes a FilterSet into the "filters" query parameter string.
 *
 * @param filterSet - Array of FilterClause objects
 * @returns URL-safe filter string (e.g., "customer:is(maersk)&status:not(active)")
 */
export function serializeFilters(filterSet: FilterSet): string {
  if (filterSet.length === 0) {
    return '';
  }

  return filterSet.map(serializeClause).filter(Boolean).join('&');
}

/**
 * Serializes a single FilterClause into DSL string format.
 * NOTE: Returns unencoded string - URLSearchParams will handle encoding when set as parameter value
 * However, we must encode DSL delimiter characters (: , ( )) to prevent parsing ambiguity
 *
 * @param clause - FilterClause object
 * @returns Serialized clause string (e.g., "customer:is(maersk,hapag)")
 */
function serializeClause(clause: FilterClause): string {
  const { facet, operator, values } = clause;

  // Encode colons in facet names to prevent confusion with operator delimiter
  const encodedFacet = facet.replace(/:/g, '%3A');

  // Allow empty values - serialize as facet:op() to keep filter in UI
  if (values.length === 0) {
    return `${encodedFacet}:${operator}()`;
  }

  // Encode DSL delimiters in values: comma, parens
  // This prevents "foo,bar" from being parsed as two values ["foo", "bar"]
  // Handle both string and numeric values (for ID-based filtering)
  const encodedValues = values.map((v) => {
    const vStr = String(v); // Convert to string if it's a number
    return vStr
      .replace(/,/g, '%2C') // Comma is value delimiter
      .replace(/\(/g, '%28') // Opening paren
      .replace(/\)/g, '%29'); // Closing paren
  });

  const valuesStr = encodedValues.join(',');
  return `${encodedFacet}:${operator}(${valuesStr})`;
}
